/**
 * components/client-detail/ClientHistoryTab.tsx
 *
 * History tab for the client detail page.
 * Displays a chronological timeline of form-related events for a client:
 * - Form sent (creation date)
 * - Form completed (completion date)
 * - Client merge events with field-level diff and undo action
 * Events are sorted in reverse chronological order.
 * Created Mar 2026.
 * Updated Apr 2026: added merge history events (MergeHistoryEntry).
 * Fix Apr 2026: merge and form events are now interleaved into a single sorted
 *   timeline so entries appear in true reverse-chronological order.
 * Updated Apr 2026: added embedded prop — when true the outer card wrapper is
 *   suppressed so the parent container provides the border/shadow.
 */

import { Send, CheckCircle2, Clock } from 'lucide-react';
import { formatDate } from '../../utils/dateUtils';
import MergeHistoryEntryComponent from '../clients/MergeHistoryEntry';
import type { MergeHistoryEntry } from '../../types/merge';

interface RequestSummary {
  id: string;
  title: string;
  status: string;
  createdAt: string;
  completedAt: string | null;
}

interface ClientHistoryTabProps {
  requests: RequestSummary[];
  mergeHistory?: MergeHistoryEntry[];
  clientId?: string;
  onUndoMerge?: (mergeId: string) => Promise<boolean>;
  undoingMerge?: boolean;
  embedded?: boolean;
}

type TimelineItem =
  | { kind: 'event'; id: string; date: string; label: string; formTitle: string; eventType: 'sent' | 'completed' }
  | { kind: 'merge'; date: string; entry: MergeHistoryEntry };

export default function ClientHistoryTab({
  requests,
  mergeHistory = [],
  clientId,
  onUndoMerge,
  undoingMerge = false,
  embedded = false,
}: ClientHistoryTabProps) {
  const items: TimelineItem[] = [];

  for (const req of requests) {
    if (req.completedAt) {
      items.push({
        kind: 'event',
        id: `${req.id}-completed`,
        date: req.completedAt,
        label: 'Formulaire complété',
        formTitle: req.title,
        eventType: 'completed',
      });
    }
    items.push({
      kind: 'event',
      id: `${req.id}-sent`,
      date: req.createdAt,
      label: 'Formulaire envoyé',
      formTitle: req.title,
      eventType: 'sent',
    });
  }

  for (const entry of mergeHistory) {
    items.push({ kind: 'merge', date: entry.merged_at, entry });
  }

  items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-neutral-100 rounded-2xl mb-4">
          <Clock className="w-7 h-7 text-neutral-400" />
        </div>
        <p className="text-neutral-600 font-medium mb-1">Aucun historique</p>
        <p className="text-sm text-neutral-400">
          Les événements liés aux formulaires apparaîtront ici
        </p>
      </div>
    );
  }

  const content = (
    <>
      {!embedded && (
        <div className="px-6 py-4 border-b border-neutral-100 bg-neutral-50">
          <h3 className="text-sm font-semibold text-neutral-700">Historique des événements</h3>
        </div>
      )}
      <div className="px-6 py-4">
        <ol className="relative">
          {items.map((item, index) => {
            if (item.kind === 'merge') {
              return (
                <MergeHistoryEntryComponent
                  key={`merge-${item.entry.id}`}
                  entry={item.entry}
                  currentClientId={clientId ?? ''}
                  onUndo={onUndoMerge ?? (() => Promise.resolve(false))}
                  undoing={undoingMerge}
                />
              );
            }

            const isLast = index === items.length - 1;
            return (
              <li key={item.id} className="relative pl-8 pb-6 last:pb-0">
                {!isLast && (
                  <span className="absolute left-[13px] top-6 w-px h-full bg-neutral-200" />
                )}
                <div className="absolute left-0 top-0.5">
                  {item.eventType === 'completed' ? (
                    <div className="w-7 h-7 rounded-full bg-success-50 border-2 border-success-200 flex items-center justify-center">
                      <CheckCircle2 className="w-3.5 h-3.5 text-success-600" />
                    </div>
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-primary-50 border-2 border-primary-200 flex items-center justify-center">
                      <Send className="w-3 h-3 text-primary-600" />
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-0.5">
                  <p className="text-sm font-medium text-neutral-900">{item.label}</p>
                  <p className="text-sm text-neutral-500 truncate max-w-sm">{item.formTitle}</p>
                  <p className="text-xs text-neutral-400 mt-0.5">{formatDate(item.date)}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </>
  );

  if (embedded) return <>{content}</>;

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 shadow-soft overflow-hidden">
      {content}
    </div>
  );
}

export type { RequestSummary };
